import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { Chronicle, REGIONS } from "@/data/chronicles";
import RegionMarker from "@/components/RegionMarker";
import europeMapImage from "@/assets/europe-map.webp";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";

// Stable empty-chronicles array. Used as the fallback when a region has
// no chronicles for the current time-range filter. Returning a fresh `[]`
// each render would invalidate RegionMarker's memo (which compares props
// by shallow reference) and re-render every shield on every parent paint,
// causing the framer-motion pulse loop to stutter. One frozen constant
// shared across every empty region keeps the reference stable.
const EMPTY_CHRONICLES: Chronicle[] = Object.freeze([]) as unknown as Chronicle[];

interface EuropeMapProps {
  filteredChronicles: Chronicle[];
  /**
   * Callback fired when a marker is hovered or unhovered. Index.tsx lifts this
   * state so the timeline can reflect the same highlight. Pass `undefined` to
   * disable cross-component sync (e.g. on touch devices).
   */
  onRegionHoverChange?: (regionId: string | null) => void;
}

// Region marker positions (percentage coordinates of the image
// extent — see imageBox below for how those % are converted into
// container pixels). Adjusted by user direction in cm; converted to
// % using ~2% per cm on the x-axis and ~3% per cm on the y-axis,
// which holds for typical desktop viewports where the image fills
// width and slightly overflows top/bottom under object-cover.
const REGION_POSITIONS: Record<string, { x: number; y: number }> = {
  scandinavia: { x: 50, y: 28.5 },       // y +1.5 (down 0.5 cm) per latest user nudge
  britishIsles: { x: 33, y: 41 },        // y +3 (down 1 cm)
  lowCountries: { x: 40.25, y: 46.25 },  // x -1 / y +1.5 (left + down 0.5 cm each)
  france: { x: 38.5, y: 56.5 },          // y -1.5 (up 0.5 cm)
  iberia: { x: 29, y: 68.75 },           // y -4.5 (up 1.5 cm)
  italia: { x: 47.5, y: 62.5 },          // y -3 (up 1 cm)
  hre: { x: 49, y: 50.5 },               // y +3 (down 1 cm)
  easternEurope: { x: 58.5, y: 47.5 },   // shifted up 1.5 cm from prior pass
  balkans: { x: 58.75, y: 64.5 },        // x +1 / y -3 (right 0.5 cm + up 1 cm)
  panEuropean: { x: 10, y: 16.5 },       // net +1.5 from original 15: down 1.5 cm, then up 1 cm
};

// Per-screen cm offsets are no longer needed: markers are now anchored
// to the IMAGE's actual rendered extent (computed at runtime from the
// image's natural aspect ratio + the container's size — see imageBox
// below). Each REGION_POSITIONS entry is the marker's coordinates in
// the image's own % space, so the same numbers land on the same
// geographic region regardless of viewport size or aspect ratio.

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;
// Touch pan dead-zone: a single finger must travel more than this many
// pixels before the map starts panning. Stops sub-threshold finger jitter
// during a tap (e.g. on a region shield) from nudging the map — the main
// cause of pan/zoom feeling "glitchy on minor movements". Mirrors the 5px
// mouse hysteresis below, a touch larger to suit fatter touch jitter.
const TOUCH_DRAG_THRESHOLD = 8;

export default function EuropeMap({ filteredChronicles, onRegionHoverChange }: EuropeMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  // Image's natural aspect ratio (width / height). Captured on load so we
  // can anchor the inner wrapper to the image's actual extent rather than
  // to the container — that way region markers stay on top of their
  // geographic regions regardless of viewport size or aspect ratio.
  const [imageAspect, setImageAspect] = useState<number | null>(null);

  // Zoom / pan state — only used on mobile
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  // Whether we're currently in a gesture (disables CSS transition for smooth tracking)
  const [isGesturing, setIsGesturing] = useState(false);

  // Live refs that the drag handlers read from instead of closing over
  // the React state values. Without these, `handleTouchStart` /
  // `handleMouseDown` would depend on `translate` in their useCallback
  // dep lists, rebuilding the handlers on every drag tick — and the
  // window-mousemove useEffect would tear-down + re-register window
  // listeners on every state update. Refs are read at gesture START
  // (cheap, once per gesture) so the handlers themselves can stay
  // referentially stable.
  const translateRef = useRef(translate);
  useEffect(() => { translateRef.current = translate; }, [translate]);
  const scaleRef = useRef(scale);
  useEffect(() => { scaleRef.current = scale; }, [scale]);

  // RAF-coalesced setTranslate. Same problem we hit on the chronicle
  // map: touch / mouse events fire at 90–120 Hz on modern phones, so
  // every move would mean 2× React renders per display frame. The
  // reconciler can't keep up, drag visibly chunks. We stash the
  // latest desired translate in a ref and only flush to React inside
  // a requestAnimationFrame — naturally caps re-renders at the
  // display refresh rate.
  // RAF-coalesced setter for BOTH scale and translate, flushed together in
  // ONE commit per frame. Pinch changes scale + translate on the same tick;
  // if they land in separate React commits (scale immediately, translate in
  // a RAF) the two halves of the transform desync within a frame and the map
  // visibly jitters. Keeping them in one pending object — and one flush —
  // makes every transform atomic. The live refs are updated optimistically
  // so the NEXT move-tick computes from the latest intended transform, not
  // the React state that only catches up after the flush (which otherwise
  // makes incremental pinch math drift).
  const pendingTransformRef = useRef<{ scale: number; x: number; y: number } | null>(null);
  const transformRafRef = useRef<number | null>(null);
  const scheduleTransform = useCallback((next: { scale: number; x: number; y: number }) => {
    pendingTransformRef.current = next;
    scaleRef.current = next.scale;
    translateRef.current = { x: next.x, y: next.y };
    if (transformRafRef.current !== null) return;
    transformRafRef.current = requestAnimationFrame(() => {
      transformRafRef.current = null;
      const p = pendingTransformRef.current;
      if (p) {
        setScale(p.scale);
        setTranslate({ x: p.x, y: p.y });
        pendingTransformRef.current = null;
      }
    });
  }, []);
  const flushTransform = useCallback(() => {
    if (transformRafRef.current !== null) {
      cancelAnimationFrame(transformRafRef.current);
      transformRafRef.current = null;
    }
    const p = pendingTransformRef.current;
    if (p) {
      setScale(p.scale);
      setTranslate({ x: p.x, y: p.y });
      pendingTransformRef.current = null;
    }
  }, []);

  const touchStateRef = useRef<{
    lastDist: number | null;
    lastMidX: number;
    lastMidY: number;
    isDragging: boolean;
    panning: boolean;
    startX: number;
    startY: number;
    startTx: number;
    startTy: number;
    originCx: number;
    originCy: number;
  }>({
    lastDist: null,
    lastMidX: 0,
    lastMidY: 0,
    isDragging: false,
    panning: false,
    startX: 0,
    startY: 0,
    startTx: 0,
    startTy: 0,
    originCx: 0,
    originCy: 0,
  });

  // Mouse-drag state — parallels the touch state above but for desktop.
  // `pressed` is set on mousedown; `moved` only flips true once the cursor
  // has travelled past the drag threshold. The threshold prevents an
  // accidental tiny twitch from being treated as a drag (which would
  // suppress the click on a region marker).
  const mouseStateRef = useRef<{
    pressed: boolean;
    moved: boolean;
    startX: number;
    startY: number;
    startTx: number;
    startTy: number;
  }>({
    pressed: false,
    moved: false,
    startX: 0,
    startY: 0,
    startTx: 0,
    startTy: 0,
  });

  // Track container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateSize = () => setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute the image's actual rendered bounds inside the container,
  // object-cover style: the image fills the container in BOTH axes
  // and the shorter side overflows. No letterboxing bands ever show.
  //   - On landscape desktops (container wider than image aspect):
  //     fit by width, image overflows top/bottom — vertical pan/drag
  //     is needed to see cropped regions.
  //   - On portrait phones (container taller than image aspect): fit
  //     by height, image overflows left/right — horizontal pan/drag.
  // Edge chevrons announce off-screen markers on whichever axis
  // overflows.
  const imageBox = useMemo(() => {
    const cw = containerSize.w;
    const ch = containerSize.h;
    if (!cw || !ch || !imageAspect) return null;
    const containerAspect = cw / ch;
    let width: number;
    let height: number;
    if (imageAspect > containerAspect) {
      // Image is wider than the container — fit by height, overflow width.
      height = ch;
      width = ch * imageAspect;
    } else {
      // Image is taller than the container — fit by width, overflow height.
      width = cw;
      height = cw / imageAspect;
    }
    return { left: (cw - width) / 2, top: (ch - height) / 2, width, height };
  }, [containerSize.w, containerSize.h, imageAspect]);

  // Clamp pan so the map never reveals empty space outside the image.
  // Bounds combine two sources of overflow:
  //   1. Natural object-cover overflow — the imageBox extends beyond
  //      the container in one axis at default scale.
  //   2. Pinch-zoom overflow — at scale > 1 the image grows larger
  //      still, allowing more pan range.
  // Returns the clamped translate so that the image edges always stay
  // touching or beyond the container edges (no blank gap revealed).
  const clampTranslate = useCallback((tx: number, ty: number, s?: number) => {
    const cw = containerSize.w;
    const ch = containerSize.h;
    if (!imageBox || !cw || !ch) return { x: tx, y: ty };
    // Read scale from the live ref when caller doesn't pass an explicit
    // value. Doing so means this callback doesn't have `scale` in its
    // dep list, which means it stays referentially stable across pinch
    // ticks — which in turn means the window-mousemove useEffect below
    // doesn't tear down + re-register its listeners on every pinch.
    const effectiveScale = s ?? scaleRef.current;
    const renderedW = imageBox.width * effectiveScale;
    const renderedH = imageBox.height * effectiveScale;
    const maxX = Math.max(0, (renderedW - cw) / 2);
    const maxY = Math.max(0, (renderedH - ch) / 2);
    return { x: clamp(tx, -maxX, maxX), y: clamp(ty, -maxY, maxY) };
  }, [imageBox, containerSize.w, containerSize.h]);

  // Laptop / desktop zoom helpers (wheel listener, +/-/reset buttons,
  // zoomAroundPoint helper) have been deliberately removed: the wheel
  // captured scroll meant for the chronicle dropdown panels, and the
  // brass buttons overlapped those dropdowns near the map's bottom
  // right corner. Touch pinch handlers above still update `scale` so
  // phones / tablets keep their pinch-zoom; on a laptop, scale is
  // pinned at 1 (no UI affordance to change it), which is exactly
  // what the static-map behaviour needs.

  // ── Touch handlers (pinch-zoom + drag, persistent across devices) ──
  // No `isMobile` gate: touch events only fire on touch-capable devices
  // anyway, so the same handlers serve phones AND tablets. Pinch-zoom
  // used to snap back to 1 after 400 ms (treated as inspection only);
  // it now persists, so a tablet user can zoom into a region and read
  // its markers properly. The reset-zoom button below restores the
  // default view when they're done.
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      setIsGesturing(true);
      const ts = touchStateRef.current;
      // Read current translate from the ref so this callback can stay
      // referentially stable across re-renders. (Pre-fix: depending on
      // `translate` here meant useCallback rebuilt this handler on
      // every drag tick — and React re-attached the onTouchStart prop
      // each time.)
      const cur = translateRef.current;
      // Cache the container centre (in client coords) once per gesture —
      // the transform-origin for pinch focal-point math. The container
      // doesn't move/resize mid-gesture, so reading the rect once here
      // avoids a layout read on every move tick.
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        ts.originCx = rect.left + rect.width / 2;
        ts.originCy = rect.top + rect.height / 2;
      }
      if (e.touches.length === 1) {
        const t = e.touches[0];
        ts.isDragging = true;
        ts.panning = false;
        ts.startX = t.clientX;
        ts.startY = t.clientY;
        ts.startTx = cur.x;
        ts.startTy = cur.y;
      } else if (e.touches.length === 2) {
        ts.isDragging = false;
        ts.panning = false;
        const [a, b] = [e.touches[0], e.touches[1]];
        ts.lastDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        ts.lastMidX = (a.clientX + b.clientX) / 2;
        ts.lastMidY = (a.clientY + b.clientY) / 2;
      }
    },
    [],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const ts = touchStateRef.current;
      if (e.touches.length === 2) {
        e.preventDefault();
        const [a, b] = [e.touches[0], e.touches[1]];
        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const midX = (a.clientX + b.clientX) / 2;
        const midY = (a.clientY + b.clientY) / 2;
        if (ts.lastDist !== null && ts.lastDist > 0) {
          const oldScale = scaleRef.current;
          const newScale = clamp(oldScale * (dist / ts.lastDist), MIN_SCALE, MAX_SCALE);
          const ratio = newScale / oldScale;
          const cur = translateRef.current;
          // Zoom around the finger midpoint (focal point), not the container
          // centre. With transform-origin at the centre, a content point
          // stays under the fingers iff translate' = focal - (focal - translate)*ratio
          // (focal measured relative to the centre). Without this the map
          // drifts toward the centre on every pinch — the classic "pinch
          // doesn't track my fingers" glitch.
          const fx = midX - ts.originCx;
          const fy = midY - ts.originCy;
          let nx = fx - (fx - cur.x) * ratio;
          let ny = fy - (fy - cur.y) * ratio;
          // Plus the two-finger pan (how far the midpoint itself travelled).
          nx += midX - ts.lastMidX;
          ny += midY - ts.lastMidY;
          const c = clampTranslate(nx, ny, newScale);
          scheduleTransform({ scale: newScale, x: c.x, y: c.y });
        }
        ts.lastDist = dist;
        ts.lastMidX = midX;
        ts.lastMidY = midY;
      } else if (e.touches.length === 1 && ts.isDragging) {
        // Persistent single-finger drag — works at any scale, including
        // default scale 1. This is how the player navigates to off-screen
        // markers on portrait phones where object-cover crops left/right.
        const t = e.touches[0];
        let dx = t.clientX - ts.startX;
        let dy = t.clientY - ts.startY;
        if (!ts.panning) {
          // Dead-zone: ignore sub-threshold finger jitter so a tap (e.g. on
          // a region shield) doesn't nudge the map. Promote to a pan only
          // once the finger clearly moves, then re-anchor the start at the
          // current point so the map doesn't jump by the threshold distance
          // on the first pan tick.
          if (Math.hypot(dx, dy) <= TOUCH_DRAG_THRESHOLD) return;
          ts.panning = true;
          ts.startX = t.clientX;
          ts.startY = t.clientY;
          ts.startTx = translateRef.current.x;
          ts.startTy = translateRef.current.y;
          dx = 0;
          dy = 0;
        }
        e.preventDefault();
        const c = clampTranslate(ts.startTx + dx, ts.startTy + dy, scaleRef.current);
        scheduleTransform({ scale: scaleRef.current, x: c.x, y: c.y });
      }
    },
    [clampTranslate, scheduleTransform],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const ts = touchStateRef.current;
      // Flush any pending RAF-deferred transform so the final pointer
      // position lands instead of being lost mid-frame.
      flushTransform();
      ts.lastDist = null;
      if (e.touches.length === 1) {
        // Pinch → single-finger transition: one finger lifted, one remains.
        // Re-anchor a fresh single-finger drag on the remaining finger so
        // the map keeps following it (and re-arms the dead-zone) instead of
        // freezing until every finger is lifted and re-placed.
        const t = e.touches[0];
        ts.isDragging = true;
        ts.panning = false;
        ts.startX = t.clientX;
        ts.startY = t.clientY;
        ts.startTx = translateRef.current.x;
        ts.startTy = translateRef.current.y;
      } else if (e.touches.length === 0) {
        // Last finger up — gesture over. Pinch-zoom and pan are both
        // persistent, so just clear the gesture state; the player's
        // zoom/pan choice stays until they change it or tap reset-zoom.
        ts.isDragging = false;
        ts.panning = false;
        setIsGesturing(false);
      }
    },
    [flushTransform],
  );

  // ── Desktop mouse drag handlers ──
  // Same panning gesture as touch, but driven by mousedown / mousemove
  // / mouseup. Window-level move + up listeners so the drag continues
  // even if the cursor leaves the container. A click-capture phase
  // listener swallows the click that would otherwise fire on a region
  // marker if the player let the mouse drift > 5 px while pressed.
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // primary button only
    const ms = mouseStateRef.current;
    ms.pressed = true;
    ms.moved = false;
    ms.startX = e.clientX;
    ms.startY = e.clientY;
    // Read translate from the ref so this callback can stay stable
    // (no [translate.x, translate.y] dep that would rebuild it on
    // every drag tick and re-attach the prop to the DOM).
    const cur = translateRef.current;
    ms.startTx = cur.x;
    ms.startTy = cur.y;
    setIsGesturing(true);
  }, []);

  // Window-level move + up listeners so the drag continues even if the
  // cursor leaves the container. A click-capture phase listener swallows
  // the click that would otherwise fire on a region marker if the
  // player let the mouse drift > 5 px while pressed.
  //
  // Dep list deliberately empty (besides our stable callbacks): scale
  // and clampTranslate are read live via refs/closures so we don't
  // tear down and re-register window listeners on every drag tick.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const ms = mouseStateRef.current;
      if (!ms.pressed) return;
      const dx = e.clientX - ms.startX;
      const dy = e.clientY - ms.startY;
      // 5 px hysteresis: any tiny twitch stays a "click", deliberate
      // movement promotes the gesture into a drag.
      if (!ms.moved && Math.hypot(dx, dy) > 5) ms.moved = true;
      if (ms.moved) {
        e.preventDefault();
        const c = clampTranslate(ms.startTx + dx, ms.startTy + dy, scaleRef.current);
        scheduleTransform({ scale: scaleRef.current, x: c.x, y: c.y });
      }
    };
    const onUp = () => {
      const ms = mouseStateRef.current;
      if (!ms.pressed) return;
      ms.pressed = false;
      flushTransform();
      setIsGesturing(false);
      // ms.moved is intentionally NOT reset here — the click handler
      // below clears it after consuming the drag-induced click.
    };
    const onClickCapture = (e: MouseEvent) => {
      const ms = mouseStateRef.current;
      if (ms.moved) {
        // Suppress the click that would otherwise fire on whatever
        // marker the cursor was over when the mouse was released.
        e.stopPropagation();
        e.preventDefault();
        ms.moved = false;
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("click", onClickCapture, true);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("click", onClickCapture, true);
    };
  }, [clampTranslate, scheduleTransform, flushTransform]);

  const chroniclesByRegion = useMemo(() => {
    const groups: Record<string, Chronicle[]> = {};
    for (const c of filteredChronicles) {
      if (!groups[c.regionId]) groups[c.regionId] = [];
      groups[c.regionId].push(c);
    }
    return groups;
  }, [filteredChronicles]);

  // Walk every region marker and tally how many are off-screen on each
  // side under the current pan. Drives the edge chevrons that show
  // "there's content this way" so the player on a phone knows to drag
  // (or tap the chevron) to reveal regions cropped by object-cover.
  // Only counts regions that actually have chronicle content; empty
  // ones don't deserve an indicator.
  const offScreenSides = useMemo(() => {
    const cw = containerSize.w;
    const ch = containerSize.h;
    if (!imageBox || !cw || !ch) return null;
    const margin = 24; // marker is "comfortably visible" if ≥ 24 px from any edge
    let left = 0, right = 0, top = 0, bottom = 0;
    for (const [regionId, basePos] of Object.entries(REGION_POSITIONS)) {
      if (!REGIONS[regionId as keyof typeof REGIONS]) continue;
      const list = chroniclesByRegion[regionId];
      if (!list || list.length === 0) continue;
      // Project the marker's image-coord into container coords using
      // the same math the out-of-wrapper shield rendering uses (so the
      // chevrons agree with the shields about who is off-screen).
      const cx = imageBox.left + imageBox.width / 2;
      const cy = imageBox.top + imageBox.height / 2;
      const mx = cx + (basePos.x / 100 - 0.5) * imageBox.width * scale + translate.x;
      const my = cy + (basePos.y / 100 - 0.5) * imageBox.height * scale + translate.y;
      if (mx < margin) left++;
      else if (mx > cw - margin) right++;
      if (my < margin) top++;
      else if (my > ch - margin) bottom++;
    }
    return { left, right, top, bottom };
  }, [imageBox, containerSize.w, containerSize.h, translate.x, translate.y, scale, chroniclesByRegion]);

  // Pan animate to reveal the markers off-screen on the given side.
  // The chevron tap target uses this — pans to the maximum on that
  // axis so the side's markers are pulled into the viewport. The CSS
  // transition on the inner wrapper provides smooth easing because we
  // explicitly set isGesturing=false right before mutating translate.
  const panToward = useCallback((direction: "left" | "right" | "top" | "bottom") => {
    if (!imageBox || !containerSize.w || !containerSize.h) return;
    const renderedW = imageBox.width * scale;
    const renderedH = imageBox.height * scale;
    const maxX = Math.max(0, (renderedW - containerSize.w) / 2);
    const maxY = Math.max(0, (renderedH - containerSize.h) / 2);
    setIsGesturing(false);
    setTranslate((prev) => {
      let tx = prev.x;
      let ty = prev.y;
      // The chevron points the direction the player wants to LOOK; the
      // image moves the opposite way. e.g. tap "←" → image shifts right
      // to expose left content → translate.x increases toward +maxX.
      if (direction === "left") tx = maxX;
      if (direction === "right") tx = -maxX;
      if (direction === "top") ty = maxY;
      if (direction === "bottom") ty = -maxY;
      return { x: tx, y: ty };
    });
  }, [imageBox, containerSize.w, containerSize.h, scale]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden rounded-sm touch-none ${isGesturing ? "cursor-grabbing" : "cursor-grab"}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
    >
      {/* Map content — sized to the image's actual rendered extent
          (imageBox) so all overlays and markers inside this wrapper
          live in image-coordinate space. Falls back to inset-0 while
          the image is loading and imageAspect is still unknown. The
          mobile pinch-zoom transform applies on top of that sizing. */}
      <div
        className={imageBox ? "absolute" : "absolute inset-0"}
        style={{
          ...(imageBox
            ? { left: imageBox.left, top: imageBox.top, width: imageBox.width, height: imageBox.height }
            : {}),
          // Pan + scale transform applied on every device — touch drag
          // and mouse drag both update `translate`, so desktop and
          // mobile share one rendering path. At default state (no pan,
          // scale 1) this is a no-op transform.
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: "center center",
          // Snappy during active gestures, eased after release for
          // smooth chevron-tap pans.
          // No transition while actively dragging/pinching — the layer must
          // track the pointer 1:1 (a transition lags content behind the
          // finger and reads as glitchy on small moves). Ease only on release.
          transition: isGesturing ? "none" : "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
          willChange: "transform",
        }}
      >
        <img
          src={europeMapImage}
          alt="Historical map of Europe"
          // Wrapper now matches the image's natural aspect ratio, so
          // a plain `w-full h-full` displays the image at full extent
          // without distortion. (object-cover would do the same; we
          // keep the inline w/h so the semantics are explicit.)
          className="w-full h-full"
          style={{ filter: "sepia(0.3) brightness(0.65) contrast(1.1)" }}
          draggable={false}
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalWidth && img.naturalHeight) {
              setImageAspect(img.naturalWidth / img.naturalHeight);
            }
          }}
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-background/30 pointer-events-none" />

        {/* Directional lighting — soft light from upper-left */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, hsl(43 50% 80% / 0.06) 0%, transparent 40%, hsl(0 0% 0% / 0.08) 100%)',
          }}
        />

        {/* Fold lines — horizontal */}
        <div className="absolute pointer-events-none" style={{
          top: '33%', left: 0, right: 0, height: '1px',
          background: 'linear-gradient(90deg, transparent 5%, hsl(0 0% 0% / 0.08) 20%, hsl(0 0% 100% / 0.03) 21%, hsl(0 0% 0% / 0.06) 80%, transparent 95%)',
        }} />
        <div className="absolute pointer-events-none" style={{
          top: '66%', left: 0, right: 0, height: '1px',
          background: 'linear-gradient(90deg, transparent 8%, hsl(0 0% 0% / 0.06) 25%, hsl(0 0% 100% / 0.02) 26%, hsl(0 0% 0% / 0.05) 75%, transparent 92%)',
        }} />
        {/* Fold lines — vertical */}
        <div className="absolute pointer-events-none" style={{
          left: '50%', top: 0, bottom: 0, width: '1px',
          background: 'linear-gradient(180deg, transparent 5%, hsl(0 0% 0% / 0.07) 20%, hsl(0 0% 100% / 0.025) 21%, hsl(0 0% 0% / 0.05) 80%, transparent 95%)',
        }} />

        {/* Compass rose — ocean area bottom-left */}
        <div className="absolute pointer-events-none select-none" style={{
          left: '12%', bottom: '18%', opacity: 0.12,
        }}>
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="28" stroke="hsl(43, 50%, 50%)" strokeWidth="0.5" />
            <circle cx="32" cy="32" r="20" stroke="hsl(43, 50%, 50%)" strokeWidth="0.3" />
            {/* Cardinal points */}
            <polygon points="32,4 34,28 32,24 30,28" fill="hsl(43, 50%, 50%)" />
            <polygon points="32,60 30,36 32,40 34,36" fill="hsl(43, 40%, 40%)" />
            <polygon points="4,32 28,30 24,32 28,34" fill="hsl(43, 40%, 40%)" />
            <polygon points="60,32 36,34 40,32 36,30" fill="hsl(43, 40%, 40%)" />
            {/* Intercardinals */}
            <line x1="12" y1="12" x2="52" y2="52" stroke="hsl(43, 50%, 50%)" strokeWidth="0.3" />
            <line x1="52" y1="12" x2="12" y2="52" stroke="hsl(43, 50%, 50%)" strokeWidth="0.3" />
            <text x="32" y="3" textAnchor="middle" fontSize="5" fill="hsl(43, 50%, 50%)" fontFamily="Cinzel, serif">N</text>
          </svg>
        </div>

        {/* Wind direction lines — faint, in ocean areas */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.04 }} viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M 2 25 Q 8 23, 15 26" stroke="hsl(43, 40%, 50%)" strokeWidth="0.5" vectorEffect="non-scaling-stroke" fill="none" />
          <path d="M 5 30 Q 10 28, 18 31" stroke="hsl(43, 40%, 50%)" strokeWidth="0.4" vectorEffect="non-scaling-stroke" fill="none" />
          <path d="M 3 35 Q 9 33, 14 36" stroke="hsl(43, 40%, 50%)" strokeWidth="0.3" vectorEffect="non-scaling-stroke" fill="none" />
          <path d="M 75 15 Q 82 13, 90 16" stroke="hsl(43, 40%, 50%)" strokeWidth="0.4" vectorEffect="non-scaling-stroke" fill="none" />
          <path d="M 78 20 Q 85 18, 93 21" stroke="hsl(43, 40%, 50%)" strokeWidth="0.3" vectorEffect="non-scaling-stroke" fill="none" />
        </svg>

        {/* Vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 50%, hsl(var(--background) / 0.6) 100%)",
          }}
        />

        {/* Top-right safe zone — blocks map interactions under GlobalControls */}
        <div
          className="absolute top-0 right-0 z-[85] pointer-events-auto"
          style={{ width: "100px", height: "56px" }}
        />
      </div>

      {/* Region markers — rendered OUTSIDE the transformed map wrapper
          so the SVG shields stay sharp at every zoom level. The map
          wrapper above is on its own GPU compositor layer (willChange:
          transform) which means it rasterises once at the natural size
          and bitmap-stretches on zoom; any vector content inside it
          (including a counter-scaled shield) would blur. Out here in
          the outer container the shields render in screen-pixel space
          and are vector-sharp at every zoom.

          Pan synchronisation: a single wrapper around all shields
          carries the pan as `transform: translate(tx, ty)` with the
          SAME transition timing as the map wrapper above. Both layers
          are GPU-composited and move in lockstep — no per-shield
          left/top reflow during a pan, which is what made the shields
          visibly trail the map on phones (13 shields with drop
          shadows + sonar pulses repainting on every touchmove was
          enough to lag a couple frames behind the map's transform).

          Per-shield position math: each marker has an image-coord
          (x%, y%). Compute its base screen position from imageBox +
          scale only — `translate.x/y` is NOT baked in, since the
          wrapper applies it once to all shields via transform. Pinch-
          zoom (rare) still recomputes per-shield left/top because the
          shields don't scale with the map; the trade-off is acceptable
          since the user complaint is about swipe-pan, not pinch. */}
      {imageBox && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px)`,
            // No transition while actively dragging/pinching — the layer must
          // track the pointer 1:1 (a transition lags content behind the
          // finger and reads as glitchy on small moves). Ease only on release.
          transition: isGesturing ? "none" : "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "transform",
          }}
        >
          {Object.entries(REGIONS).map(([regionId, region]) => {
            const basePos = REGION_POSITIONS[regionId];
            if (!basePos) return null;
            const px = clamp(basePos.x, 0, 100) / 100;
            const py = clamp(basePos.y, 0, 100) / 100;
            const cx = imageBox.left + imageBox.width / 2;
            const cy = imageBox.top + imageBox.height / 2;
            const shieldX = cx + (px - 0.5) * imageBox.width * scale;
            const shieldY = cy + (py - 0.5) * imageBox.height * scale;
            return (
              <div
                key={regionId}
                className="absolute pointer-events-auto"
                style={{ left: shieldX, top: shieldY }}
              >
                <RegionMarker
                  regionId={regionId}
                  regionName={region.name}
                  chronicles={chroniclesByRegion[regionId] || EMPTY_CHRONICLES}
                  onHoverChange={onRegionHoverChange}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Off-screen marker chevrons — pinned to the outer container so
          they don't pan with the map below. Each side renders a small
          brass chevron + count when there are markers cropped on that
          side under the current pan. Tap to pan the map toward that
          direction (CSS easing handles the smooth transition). */}
      {offScreenSides && offScreenSides.left > 0 && (
        <button
          type="button"
          onClick={() => panToward("left")}
          aria-label={`${offScreenSides.left} region${offScreenSides.left === 1 ? "" : "s"} off-screen to the left: tap to reveal`}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-[60] flex items-center justify-center gap-1 px-3 py-3 min-w-[44px] min-h-[44px] rounded-sm pointer-events-auto"
          style={{
            background: "linear-gradient(180deg, hsl(35, 35%, 22%), hsl(28, 25%, 14%))",
            border: "1px solid hsl(43, 50%, 35% / 0.55)",
            boxShadow: "0 2px 6px hsl(0 0% 0% / 0.55)",
          }}
        >
          <ChevronLeft className="w-3.5 h-3.5" style={{ color: "hsl(var(--gold))" }} />
          <span className="text-[10px] font-display tracking-tight leading-none" style={{ color: "hsl(var(--gold-dim))" }}>{offScreenSides.left}</span>
        </button>
      )}
      {offScreenSides && offScreenSides.right > 0 && (
        <button
          type="button"
          onClick={() => panToward("right")}
          aria-label={`${offScreenSides.right} region${offScreenSides.right === 1 ? "" : "s"} off-screen to the right: tap to reveal`}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-[60] flex items-center justify-center gap-1 px-3 py-3 min-w-[44px] min-h-[44px] rounded-sm pointer-events-auto"
          style={{
            background: "linear-gradient(180deg, hsl(35, 35%, 22%), hsl(28, 25%, 14%))",
            border: "1px solid hsl(43, 50%, 35% / 0.55)",
            boxShadow: "0 2px 6px hsl(0 0% 0% / 0.55)",
          }}
        >
          <span className="text-[10px] font-display tracking-tight leading-none" style={{ color: "hsl(var(--gold-dim))" }}>{offScreenSides.right}</span>
          <ChevronRight className="w-3.5 h-3.5" style={{ color: "hsl(var(--gold))" }} />
        </button>
      )}
      {offScreenSides && offScreenSides.top > 0 && (
        <button
          type="button"
          onClick={() => panToward("top")}
          aria-label={`${offScreenSides.top} region${offScreenSides.top === 1 ? "" : "s"} off-screen above: tap to reveal`}
          className="absolute top-2 left-1/2 -translate-x-1/2 z-[60] flex items-center justify-center gap-1 px-3 py-3 min-w-[44px] min-h-[44px] rounded-sm pointer-events-auto"
          style={{
            background: "linear-gradient(180deg, hsl(35, 35%, 22%), hsl(28, 25%, 14%))",
            border: "1px solid hsl(43, 50%, 35% / 0.55)",
            boxShadow: "0 2px 6px hsl(0 0% 0% / 0.55)",
          }}
        >
          <ChevronUp className="w-3.5 h-3.5" style={{ color: "hsl(var(--gold))" }} />
          <span className="text-[10px] font-display tracking-tight leading-none" style={{ color: "hsl(var(--gold-dim))" }}>{offScreenSides.top}</span>
        </button>
      )}
      {offScreenSides && offScreenSides.bottom > 0 && (
        <button
          type="button"
          onClick={() => panToward("bottom")}
          aria-label={`${offScreenSides.bottom} region${offScreenSides.bottom === 1 ? "" : "s"} off-screen below: tap to reveal`}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 z-[60] flex items-center justify-center gap-1 px-3 py-3 min-w-[44px] min-h-[44px] rounded-sm pointer-events-auto pb-safe"
          style={{
            background: "linear-gradient(180deg, hsl(35, 35%, 22%), hsl(28, 25%, 14%))",
            border: "1px solid hsl(43, 50%, 35% / 0.55)",
            boxShadow: "0 2px 6px hsl(0 0% 0% / 0.55)",
          }}
        >
          <span className="text-[10px] font-display tracking-tight leading-none" style={{ color: "hsl(var(--gold-dim))" }}>{offScreenSides.bottom}</span>
          <ChevronDown className="w-3.5 h-3.5" style={{ color: "hsl(var(--gold))" }} />
        </button>
      )}

      {/* Laptop / desktop zoom UI intentionally removed. The wheel
          listener intercepted scroll inside the chronicle dropdown
          panels; the +/− brass-button stack overlapped the open
          dropdowns near the bottom-right. Touch pinch on phones and
          tablets is preserved (handlers above). Laptop / desktop
          users get a fixed-extent map — every region is reachable at
          default zoom and the chronicle picker stays uninterrupted. */}
    </div>
  );
}
